import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Table, Tabs, Tab, Stack, Spinner } from "react-bootstrap";
import Select from "react-select";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import LoadingPage from "../../Components/LoadingPage/LoadingPage";
import { POPUP_CLASS } from "../../constants/appConstants";
import { getAllDocumentsThunk } from "../../store/documents/thunkMiddlewares";
import { getAllUsersThunk } from "../../store/user/thunkMiddlewares";
import { DocumentDto } from "../../types/document";
import { AssignedDto, UserDto } from "../../types/user.dto";
import { TRAINING_KPI_PAGE_CLASS, TRAINING_KPI_PANEL, TRAINING_KPI_TABLE } from "./constants";
import "./style.css";
import { updateTrainingClassificationAPI } from "../../apiRequests/documents/updateTrainingClassificationAPI";
import { updateOnTimeOverrideAPI } from "../../apiRequests/assigned/updateOnTimeOverrideAPI";
import { updateTrainingClassificationOverrideAPI } from "../../apiRequests/assigned/updateTrainingClassificationOverrideAPI";

type Classification = "CRITICAL" | "NON_CRITICAL";
type OnTimeResult = "PASS" | "FAIL" | "PENDING";

const STATUS_ASSIGNED = "ASSIGNED";
const STATUS_PASSED = "PASSED";
const STATUS_FAILED = "FAILED";
const QUIZ_TYPE_MCQ = "MCQ";

type AssignedAny = AssignedDto & {
    _id?: string;
    onTimeOverride?: "PASS" | "FAIL" | null;
    trainingClassificationOverride?: "CRITICAL" | "NON_CRITICAL" | null;
    resultHistoryTrack?: Array<{ status: string; date?: string }>;
    createdAt?: any;
    updatedAt?: any;
};

function TrainingKPIPage() {
    const dispatch = useAppDispatch();
    const documentsState = useAppSelector((store) => store.documents);
    const userState = useAppSelector((store) => store.user);

    const [fromDate, setFromDate] = useState<string>(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        return date.toISOString().split("T")[0];
    });
    const [toDate, setToDate] = useState<string>(() => new Date().toISOString().split("T")[0]);
    const fromDateObj = useMemo(() => {
        const d = new Date(fromDate);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [fromDate]);
    const toDateObj = useMemo(() => {
        const d = new Date(toDate);
        d.setHours(23, 59, 59, 999);
        return d;
    }, [toDate]);

    const handleFromDateChange = (value: string) => {
        // Ensure fromDate is never after toDate
        setFromDate(value);
        if (value && toDate && value > toDate) {
            setToDate(value);
        }
    };

    const handleToDateChange = (value: string) => {
        // Ensure toDate is never before fromDate
        setToDate(value);
        if (value && fromDate && value < fromDate) {
            setFromDate(value);
        }
    };

    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
    const [showDetail, setShowDetail] = useState(false);

    const [selectedVersionKeys, setSelectedVersionKeys] = useState<Record<string, boolean>>({});
    const [bulkClassification, setBulkClassification] = useState<Classification>("NON_CRITICAL");

    const [sortColumn, setSortColumn] = useState<string>("totalAssigned");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

    // KPI Report modal (only place where users are selectable)
    const [showKpiModal, setShowKpiModal] = useState(false);
    const [kpiModalSelectedIds, setKpiModalSelectedIds] = useState<Set<string>>(new Set());
    const [kpiModalSearch, setKpiModalSearch] = useState("");

    // Row-level loading for overrides in detail modal
    const [updatingOnTimeId, setUpdatingOnTimeId] = useState<string | null>(null);

    // Per-document bulk classification modal state
    const [docUserModalKey, setDocUserModalKey] = useState<string | null>(null);
    const [docUserSelectedIds, setDocUserSelectedIds] = useState<Set<string>>(new Set());
    const [docUserBulkClassification, setDocUserBulkClassification] = useState<Classification>("CRITICAL");
    const [docUserUpdating, setDocUserUpdating] = useState(false);

    useEffect(() => {
        dispatch(getAllDocumentsThunk());
        dispatch(getAllUsersThunk());
    }, []);

    const classificationMap = useMemo(() => buildClassificationMap(documentsState.documents), [documentsState.documents]);
    const documentMap = useMemo(() => {
        const m = new Map<string, DocumentDto>();
        documentsState.documents.forEach((d: DocumentDto) => m.set(d.confluenceDocumentId, d));
        return m;
    }, [documentsState.documents]);
    const effectiveOnMap = useMemo(() => buildEffectiveOnMap(documentsState.documents), [documentsState.documents]);
    const users = userState.users || [];

    // Keep selectedUser in sync with latest store data so detail modal reflects overrides immediately
    useEffect(() => {
        if (!selectedUser || !users.length) return;
        const fresh = users.find((u) => u.accountId === selectedUser.accountId);
        if (fresh && fresh !== selectedUser) {
            setSelectedUser(fresh);
        }
    }, [users, selectedUser]);

    const summaryRows = useMemo(() => {
        return users.map((u) => {
            // Filter assignments: completion date within the date range
            const assignedInRange = filterAssignedInRange(u, fromDateObj, toDateObj);
            const totalAssigned = assignedInRange.length;

            // Evaluate status as of the end date
            const completed = assignedInRange.filter((a) => {
                const status = getStatusAsOf(a as AssignedAny, toDateObj);
                return status === STATUS_PASSED;
            }).length;
            const pending = totalAssigned - completed;

            let criticalAssigned = 0;
            let criticalCompleted = 0;
            let criticalOnTime = 0;
            let nonCriticalAssigned = 0;
            let nonCriticalCompleted = 0;

            // On-time (evaluated as of end date)
            let onTimePass = 0;
            let onTimeFail = 0;
            let onTimePending = 0;

            // Quiz effectiveness (MCQ)
            const quizzesAssigned = assignedInRange.filter((a) => isQuizAssigned(a as AssignedAny)).length;
            const quizzesCompleted = assignedInRange.filter((a) => {
                return isQuizAssigned(a as AssignedAny) && getStatusAsOf(a as AssignedAny, toDateObj) === STATUS_PASSED;
            }).length;
            const passAttempt = { a1: 0, a2: 0, a3: 0, a4: 0 };

            assignedInRange.forEach((a0) => {
                const a = a0 as AssignedAny;
                const baseCls = classificationMap.get(docVersionKey(a.documentId, a.documentVersion.version)) || "NON_CRITICAL";
                const cls = a.trainingClassificationOverride || baseCls;
                const status = getStatusAsOf(a, toDateObj);
                const isCompleted = status === STATUS_PASSED;
                const docKey = docVersionKey(a.documentId, a.documentVersion.version);
                const baseDate = effectiveOnMap.get(docKey) || parseDateSafe(a.createdAt) || undefined;

                if (cls === "CRITICAL") {
                    criticalAssigned++;
                    if (isCompleted) {
                        criticalCompleted++;
                        // Check if critical training was completed on time
                        const ot = getOnTimeEffectiveResult(a, toDateObj, 30, baseDate).result;
                        if (ot === "PASS") criticalOnTime++;
                    }
                } else {
                    nonCriticalAssigned++;
                    if (isCompleted) nonCriticalCompleted++;
                }

                // On-time evaluation as of end date
                const ot = getOnTimeEffectiveResult(a, toDateObj, 30, baseDate).result;
                if (ot === "PASS") onTimePass++;
                else if (ot === "FAIL") onTimeFail++;
                else onTimePending++;

                if (isQuizAssigned(a) && isCompleted) {
                    const attempt = getAttemptPassedOn(a);
                    if (attempt === 1) passAttempt.a1++;
                    else if (attempt === 2) passAttempt.a2++;
                    else if (attempt === 3) passAttempt.a3++;
                    else passAttempt.a4++; // 4+
                }
            });

            // Calculate percentages
            const completionPercent = totalAssigned > 0 ? (completed / totalAssigned) * 100 : 0;
            const onTimePercent = totalAssigned > 0 ? (onTimePass / totalAssigned) * 100 : 0;
            const criticalCompletionPercent = criticalAssigned > 0 ? (criticalCompleted / criticalAssigned) * 100 : 0;
            const criticalOnTimePercent = criticalAssigned > 0 ? (criticalOnTime / criticalAssigned) * 100 : 0;

            // Determine Training Competency Status
            // Pass if: all critical trainings completed AND all critical trainings completed on time
            const competencyStatus =
                criticalAssigned > 0
                    ? criticalCompleted === criticalAssigned && criticalOnTime === criticalAssigned
                        ? "Pass"
                        : "Fail"
                    : totalAssigned > 0
                      ? completed === totalAssigned && onTimePass === totalAssigned
                          ? "Pass"
                          : "Fail"
                      : "N/A";

            return {
                user: u,
                totalAssigned,
                completed,
                pending,
                onTimePass,
                onTimeFail,
                onTimePending,
                criticalAssigned,
                criticalCompleted,
                criticalOnTime,
                nonCriticalAssigned,
                nonCriticalCompleted,
                quizzesAssigned,
                quizzesCompleted,
                passAttempt,
                completionPercent,
                onTimePercent,
                criticalCompletionPercent,
                criticalOnTimePercent,
                competencyStatus,
            };
        });
    }, [users, fromDateObj, toDateObj, classificationMap, effectiveOnMap]);

    const sortedSummaryRows = useMemo(() => {
        const sorted = [...summaryRows];
        sorted.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortColumn) {
                case "employee":
                    aValue = a.user.displayName.toLowerCase();
                    bValue = b.user.displayName.toLowerCase();
                    break;
                case "totalAssigned":
                    aValue = a.totalAssigned;
                    bValue = b.totalAssigned;
                    break;
                case "completed":
                    aValue = a.completed;
                    bValue = b.completed;
                    break;
                case "completionPercent":
                    aValue = a.completionPercent;
                    bValue = b.completionPercent;
                    break;
                case "onTimePercent":
                    aValue = a.onTimePercent;
                    bValue = b.onTimePercent;
                    break;
                case "pending":
                    aValue = a.pending;
                    bValue = b.pending;
                    break;
                case "onTimePass":
                    aValue = a.onTimePass;
                    bValue = b.onTimePass;
                    break;
                case "onTimeFail":
                    aValue = a.onTimeFail;
                    bValue = b.onTimeFail;
                    break;
                case "criticalAssigned":
                    aValue = a.criticalAssigned;
                    bValue = b.criticalAssigned;
                    break;
                case "criticalCompleted":
                    aValue = a.criticalCompleted;
                    bValue = b.criticalCompleted;
                    break;
                case "criticalCompletionPercent":
                    aValue = a.criticalCompletionPercent;
                    bValue = b.criticalCompletionPercent;
                    break;
                case "criticalOnTime":
                    aValue = a.criticalOnTime;
                    bValue = b.criticalOnTime;
                    break;
                case "criticalOnTimePercent":
                    aValue = a.criticalOnTimePercent;
                    bValue = b.criticalOnTimePercent;
                    break;
                case "competencyStatus":
                    aValue = a.competencyStatus === "Pass" ? 1 : a.competencyStatus === "Fail" ? 0 : -1;
                    bValue = b.competencyStatus === "Pass" ? 1 : b.competencyStatus === "Fail" ? 0 : -1;
                    break;
                case "critical":
                    aValue = a.criticalAssigned > 0 ? a.criticalCompleted / a.criticalAssigned : 0;
                    bValue = b.criticalAssigned > 0 ? b.criticalCompleted / b.criticalAssigned : 0;
                    break;
                case "nonCritical":
                    aValue = a.nonCriticalAssigned > 0 ? a.nonCriticalCompleted / a.nonCriticalAssigned : 0;
                    bValue = b.nonCriticalAssigned > 0 ? b.nonCriticalCompleted / b.nonCriticalAssigned : 0;
                    break;
                case "quizzesAssigned":
                    aValue = a.quizzesAssigned;
                    bValue = b.quizzesAssigned;
                    break;
                case "quizzesCompleted":
                    aValue = a.quizzesCompleted;
                    bValue = b.quizzesCompleted;
                    break;
                case "passAt1":
                    aValue = a.passAttempt.a1;
                    bValue = b.passAttempt.a1;
                    break;
                case "passAt2":
                    aValue = a.passAttempt.a2;
                    bValue = b.passAttempt.a2;
                    break;
                case "passAt3":
                    aValue = a.passAttempt.a3;
                    bValue = b.passAttempt.a3;
                    break;
                case "passAt4":
                    aValue = a.passAttempt.a4;
                    bValue = b.passAttempt.a4;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
            if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [summaryRows, sortColumn, sortDirection]);

    const documentSummaryRows = useMemo(() => {
        const map = new Map<
            string,
            {
                key: string;
                documentId: string;
                documentName: string;
                version: string;
                classification: Classification;
                effectiveOnDate: Date | null;
                totalAssigned: number;
                completed: number;
                onTimePass: number;
                onTimeFail: number;
                onTimePending: number;
            }
        >();

        users.forEach((u) => {
            const assignedInRange = filterAssignedInRange(u, fromDateObj, toDateObj);
            assignedInRange.forEach((a0) => {
                const a = a0 as AssignedAny;
                const doc = documentMap.get(a.documentId);
                const name = doc?.name || a.documentId;
                const key = docVersionKey(a.documentId, a.documentVersion.version);
                const baseCls = classificationMap.get(key) || "NON_CRITICAL";
                const cls = a.trainingClassificationOverride || baseCls;
                const effectiveOnDate = effectiveOnMap.get(key) || null;
                const status = getStatusAsOf(a, toDateObj);
                const isCompleted = status === STATUS_PASSED;
                const baseDate = effectiveOnDate || parseDateSafe(a.createdAt) || undefined;
                const ot = getOnTimeEffectiveResult(a as AssignedAny, toDateObj, 30, baseDate).result;

                let row = map.get(key);
                if (!row) {
                    row = {
                        key,
                        documentId: a.documentId,
                        documentName: name,
                        version: a.documentVersion.version,
                        classification: cls,
                        effectiveOnDate,
                        totalAssigned: 0,
                        completed: 0,
                        onTimePass: 0,
                        onTimeFail: 0,
                        onTimePending: 0,
                    };
                    map.set(key, row);
                }

                row.totalAssigned += 1;
                if (isCompleted) {
                    row.completed += 1;
                }
                if (ot === "PASS") row.onTimePass += 1;
                else if (ot === "FAIL") row.onTimeFail += 1;
                else row.onTimePending += 1;
            });
        });

        return Array.from(map.values()).sort((a, b) =>
            (a.documentName + a.version).localeCompare(b.documentName + b.version),
        );
    }, [users, fromDateObj, toDateObj, classificationMap, documentMap, effectiveOnMap]);

    const docUserModalRows = useMemo(() => {
        if (!docUserModalKey) return [];
        const [documentId, version] = docUserModalKey.split("::");
        const rows: {
            assigned: AssignedAny;
            user: UserDto;
            classification: Classification;
        }[] = [];

        users.forEach((u) => {
            const assignedInRange = filterAssignedInRange(u, fromDateObj, toDateObj);
            assignedInRange.forEach((a0) => {
                const a = a0 as AssignedAny;
                if (a.documentId === documentId && a.documentVersion.version === version) {
                    const key = docVersionKey(a.documentId, a.documentVersion.version);
                    const baseCls = classificationMap.get(key) || "NON_CRITICAL";
                    const cls = (a.trainingClassificationOverride || baseCls) as Classification;
                    rows.push({
                        assigned: a,
                        user: u,
                        classification: cls,
                    });
                }
            });
        });

        return rows.sort((a, b) => a.user.displayName.localeCompare(b.user.displayName));
    }, [docUserModalKey, users, fromDateObj, toDateObj, classificationMap]);

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("desc");
        }
    };

    const getSortIcon = (column: string) => {
        if (sortColumn !== column) return " ↕";
        return sortDirection === "asc" ? " ↑" : " ↓";
    };

    const openKpiModal = () => {
        // Start with no trainees selected; user must choose explicitly
        setKpiModalSelectedIds(new Set());
        setKpiModalSearch("");
        setShowKpiModal(true);
    };

    const kpiModalOptions = useMemo(
        () => sortedSummaryRows.filter((r) => r.user.displayName.toLowerCase().includes(kpiModalSearch.toLowerCase())),
        [sortedSummaryRows, kpiModalSearch],
    );

    const handleKpiModalRemoveUser = (userId: string) => {
        setKpiModalSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
        });
    };

    const handleKpiModalSelectChange = (selected: any) => {
        const arr = Array.isArray(selected) ? selected : [];
        const ids = arr.map((o: any) => o.value as string);
        setKpiModalSelectedIds(new Set(ids));
    };

    const handleKpiModalClear = () => {
        setKpiModalSelectedIds(new Set());
    };

    const handleKpiModalSelectAllNonNA = () => {
        const ids = sortedSummaryRows.filter((r) => r.competencyStatus !== "N/A").map((r) => r.user.accountId);
        setKpiModalSelectedIds(new Set(ids));
    };

    const kpiModalKPI = useMemo(() => {
        if (kpiModalSelectedIds.size === 0) return null;
        const selected = sortedSummaryRows.filter((r) => kpiModalSelectedIds.has(r.user.accountId));
        const passed = selected.filter((r) => r.competencyStatus === "Pass").length;
        const total = selected.length;
        return {
            total,
            passed,
            failed: total - passed,
            passRate: total > 0 ? (passed / total) * 100 : 0,
        };
    }, [sortedSummaryRows, kpiModalSelectedIds]);

    const exportSummaryExcel = () => {
        // Export the same trainees as selected in the KPI Report modal
        const rowsToExport = sortedSummaryRows.filter((r) => kpiModalSelectedIds.has(r.user.accountId));

        if (rowsToExport.length === 0) {
            alert("Please select at least one trainee in KPI Report before exporting.");
            return;
        }

        // Use existing KPI calculation from the modal if available; otherwise compute here
        const exportKPI = kpiModalKPI
            ? {
                  totalSelected: kpiModalKPI.total,
                  passed: kpiModalKPI.passed,
                  failed: kpiModalKPI.failed,
                  passRate: kpiModalKPI.passRate,
              }
            : (() => {
                  const passedCount = rowsToExport.filter((r) => r.competencyStatus === "Pass").length;
                  return {
                      totalSelected: rowsToExport.length,
                      passed: passedCount,
                      failed: rowsToExport.length - passedCount,
                      passRate: (passedCount / rowsToExport.length) * 100,
                  };
              })();

        // Excel-compatible .xls via HTML table (no extra deps)
        const rows = rowsToExport
            .map((r) => {
                return `<tr>
                    <td>${escapeHtml(r.user.displayName)}</td>
                    <td>${r.totalAssigned}</td>
                    <td>${r.completed}</td>
                    <td>${r.completionPercent.toFixed(1)}%</td>
                    <td>${r.onTimePercent.toFixed(1)}%</td>
                    <td>${r.pending}</td>
                    <td>${r.onTimePass}</td>
                    <td>${r.onTimeFail}</td>
                    <td>${r.criticalAssigned}</td>
                    <td>${r.criticalCompleted}</td>
                    <td>${r.criticalCompletionPercent.toFixed(1)}%</td>
                    <td>${r.criticalOnTime}</td>
                    <td>${r.criticalOnTimePercent.toFixed(1)}%</td>
                    <td>${r.competencyStatus}</td>
                    <td>${r.nonCriticalCompleted}/${r.nonCriticalAssigned}</td>
                    <td>${r.quizzesAssigned}</td>
                    <td>${r.quizzesCompleted}</td>
                    <td>${r.passAttempt.a1}</td>
                    <td>${r.passAttempt.a2}</td>
                    <td>${r.passAttempt.a3}</td>
                    <td>${r.passAttempt.a4}</td>
                </tr>`;
            })
            .join("");

        const kpiSummary = `<div style="margin-bottom: 20px; padding: 10px; background-color: #f8f9fa; border: 1px solid #dee2e6;">
            <h3>Overall KPI Summary</h3>
            <p><strong>Total Employees:</strong> ${exportKPI.totalSelected}</p>
            <p><strong>Pass Rate:</strong> ${exportKPI.passRate.toFixed(2)}%</p>
            <p><strong>Passed:</strong> ${exportKPI.passed} | <strong>Failed:</strong> ${exportKPI.failed}</p>
            <p><strong>Completion date range:</strong> ${formatDate(fromDateObj)} to ${formatDate(toDateObj)}</p>
        </div>`;

        const html = `<!doctype html>
<html>
<head>
    <meta charset="utf-8" />
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        table { border-collapse: collapse; width: 100%; }
        th { background-color: #4CAF50; color: white; padding: 12px; text-align: left; font-weight: bold; }
        td { border: 1px solid #ddd; padding: 8px; }
        tr:nth-child(even) { background-color: #f2f2f2; }
        tr:hover { background-color: #ddd; }
    </style>
</head>
<body>
    <h1>Training KPI Report</h1>
    ${kpiSummary}
    <table border="1">
        <thead>
            <tr>
                <th>Employee</th>
                <th>Total Assigned</th>
                <th>Completed</th>
                <th>Completion %</th>
                <th>On-Time %</th>
                <th>Pending</th>
                <th>Completed On Time</th>
                <th>On-time FAIL</th>
                <th>Critical Assigned</th>
                <th>Critical Completed</th>
                <th>Critical Completion %</th>
                <th>Critical On Time</th>
                <th>Critical On-Time %</th>
                <th>Training Competency Status</th>
                <th>Non-critical (C/A)</th>
                <th>Quiz Assigned</th>
                <th>Quiz Completed</th>
                <th>Pass @1</th>
                <th>Pass @2</th>
                <th>Pass @3</th>
                <th>Pass @4+</th>
            </tr>
        </thead>
        <tbody>${rows}</tbody>
    </table>
</body>
</html>`;

        downloadBlob(html, `training-kpi-selected-${fromDate}-to-${toDate}.xls`, "application/vnd.ms-excel");
    };

    const openUserDetail = (u: UserDto) => {
        setSelectedUser(u);
        setShowDetail(true);
    };

    const assignedDetailRows = useMemo(() => {
        if (!selectedUser) return [];
        const assignedInRange = filterAssignedInRange(selectedUser, fromDateObj, toDateObj);
        return assignedInRange
            .map((a0) => {
                const a = a0 as AssignedAny;
                const doc = documentMap.get(a.documentId);
                const name = doc?.name || a.documentId;
                const baseCls = classificationMap.get(docVersionKey(a.documentId, a.documentVersion.version)) || "NON_CRITICAL";
                const cls = a.trainingClassificationOverride || baseCls;
                const docKey = docVersionKey(a.documentId, a.documentVersion.version);
                const effectiveOnDate = effectiveOnMap.get(docKey) || null;
                const signDate = parseDateSafe(a.createdAt);
                const baseDate = effectiveOnDate || signDate || null;
                // Evaluate as of end date
                const completionDate = getCompletionDateAsOf(a, toDateObj);
                const onTime = getOnTimeEffectiveResult(a, toDateObj, 30, baseDate || undefined);
                const baseStatus = getStatusAsOf(a, toDateObj);
                const daysOpen = baseDate
                    ? Math.max(0, Math.floor((toDateObj.getTime() - baseDate.getTime()) / (24 * 60 * 60 * 1000)))
                    : undefined;
                const risk = typeof daysOpen === "number" ? getRiskColor(daysOpen) : "NONE";
                const attempt = isQuizAssigned(a) ? getAttemptPassedOn(a) : null;
                return {
                    assigned: a,
                    name,
                    version: a.documentVersion.version,
                    quizType: a.documentVersion.quizType,
                    statusAsOf: baseStatus,
                    trainingClassification: cls,
                    baseTrainingClassification: baseCls,
                    effectiveOnDate,
                    signDate,
                    completionDate,
                    onTime,
                    daysOpen,
                    risk,
                    attempt,
                };
            })
            .sort((x, y) => (x.name + x.version).localeCompare(y.name + y.version));
    }, [selectedUser, fromDateObj, toDateObj, classificationMap, documentMap, effectiveOnMap]);

    const handleOverrideChange = async (assigned: AssignedAny, override: "PASS" | "FAIL" | "AUTO") => {
        if (!assigned._id) return;
        try {
            setUpdatingOnTimeId(assigned._id);
            await updateOnTimeOverrideAPI({
                assignedId: assigned._id,
                onTimeOverride: override === "AUTO" ? null : override,
            });
            await dispatch(getAllUsersThunk());
        } finally {
            setUpdatingOnTimeId((current) => (current === assigned._id ? null : current));
        }
    };

    const handleClassificationOverrideChange = async (assigned: AssignedAny, classification: "CRITICAL" | "NON_CRITICAL" | "AUTO") => {
        if (!assigned._id) return;
        await updateTrainingClassificationOverrideAPI({
            assignedId: assigned._id,
            trainingClassificationOverride: classification === "AUTO" ? null : classification,
        });
        dispatch(getAllUsersThunk());
    };

    const allDocVersions = useMemo(() => {
        const rows: { key: string; documentId: string; documentName: string; version: string; classification: Classification }[] = [];
        documentsState.documents.forEach((d: any) => {
            (d.allVersions || []).forEach((v: any) => {
                const key = docVersionKey(d.confluenceDocumentId, v.version);
                rows.push({
                    key,
                    documentId: d.confluenceDocumentId,
                    documentName: d.name,
                    version: v.version,
                    classification: v.trainingClassification || "NON_CRITICAL",
                });
            });
        });
        return rows.sort((a, b) => (a.documentName + a.version).localeCompare(b.documentName + b.version));
    }, [documentsState.documents]);

    const toggleVersionKey = (key: string) => {
        setSelectedVersionKeys((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const setBulkClassificationForSelected = async () => {
        const selected = Object.entries(selectedVersionKeys)
            .filter(([, v]) => v)
            .map(([k]) => k);
        if (!selected.length) return;
        const updates = selected.map((k) => {
            const [documentId, version] = k.split("::");
            return { confluenceDocumentId: documentId, version, trainingClassification: bulkClassification };
        });
        await updateTrainingClassificationAPI(updates);
        setSelectedVersionKeys({});
        dispatch(getAllDocumentsThunk());
    };

    const renderLoading = () => {
        const isLoading = documentsState.isLoading || userState.isLoading;
        return (
            isLoading && (
                <div className={POPUP_CLASS}>
                    <LoadingPage />
                </div>
            )
        );
    };

    return (
        <div className={TRAINING_KPI_PAGE_CLASS}>
            {renderLoading()}

            <div className={TRAINING_KPI_PANEL}>
                <Stack direction="horizontal" gap={3}>
                    <div>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control type="date" value={fromDate} onChange={(e) => handleFromDateChange(e.target.value)} />
                    </div>
                    <div>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control type="date" value={toDate} onChange={(e) => handleToDateChange(e.target.value)} />
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                        <Form.Label>Actions</Form.Label>
                        <div style={{ display: "flex", gap: 8 }}>
                            <Button variant="info" onClick={openKpiModal}>
                                📊 KPI Report
                            </Button>
                        </div>
                    </div>
                </Stack>
                <div style={{ marginTop: 8, color: "#666" }}>
                    Completion date range: <b>{formatDate(fromDateObj)}</b> to <b>{formatDate(toDateObj)}</b> (on-time: sign date + 30 days)
                </div>
            </div>

            <Tabs defaultActiveKey="summary" className={TRAINING_KPI_PANEL}>
                <Tab eventKey="summary" title="KPI Summary">
                    <Table striped bordered hover size="sm" className={TRAINING_KPI_TABLE}>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("employee")}>
                                    Employee {getSortIcon("employee")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("totalAssigned")}>
                                    Total Assigned {getSortIcon("totalAssigned")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("completed")}>
                                    Completed {getSortIcon("completed")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("completionPercent")}>
                                    Completion % {getSortIcon("completionPercent")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("onTimePercent")}>
                                    On-Time % {getSortIcon("onTimePercent")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("pending")}>
                                    Pending {getSortIcon("pending")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("onTimePass")}>
                                    Completed On Time {getSortIcon("onTimePass")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("onTimeFail")}>
                                    On-time FAIL {getSortIcon("onTimeFail")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("criticalAssigned")}>
                                    Critical Assigned {getSortIcon("criticalAssigned")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("criticalCompleted")}>
                                    Critical Completed {getSortIcon("criticalCompleted")}
                                </th>
                                <th
                                    style={{ cursor: "pointer", userSelect: "none" }}
                                    onClick={() => handleSort("criticalCompletionPercent")}
                                >
                                    Critical Completion % {getSortIcon("criticalCompletionPercent")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("criticalOnTime")}>
                                    Critical On Time {getSortIcon("criticalOnTime")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("criticalOnTimePercent")}>
                                    Critical On-Time % {getSortIcon("criticalOnTimePercent")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("competencyStatus")}>
                                    Training Competency Status {getSortIcon("competencyStatus")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("nonCritical")}>
                                    Non-critical (C/A) {getSortIcon("nonCritical")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("quizzesAssigned")}>
                                    Quiz Assigned {getSortIcon("quizzesAssigned")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("quizzesCompleted")}>
                                    Quiz Completed {getSortIcon("quizzesCompleted")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("passAt1")}>
                                    Pass @1 {getSortIcon("passAt1")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("passAt2")}>
                                    Pass @2 {getSortIcon("passAt2")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("passAt3")}>
                                    Pass @3 {getSortIcon("passAt3")}
                                </th>
                                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("passAt4")}>
                                    Pass @4+ {getSortIcon("passAt4")}
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedSummaryRows.map((r) => (
                                <tr key={r.user.accountId + r.competencyStatus}>
                                    <td>{r.user.displayName}</td>
                                    <td>{r.totalAssigned}</td>
                                    <td>{r.completed}</td>
                                    <td>{r.completionPercent.toFixed(1)}%</td>
                                    <td>{r.onTimePercent.toFixed(1)}%</td>
                                    <td>{r.pending}</td>
                                    <td>{r.onTimePass}</td>
                                    <td>{r.onTimeFail}</td>
                                    <td>{r.criticalAssigned}</td>
                                    <td>{r.criticalCompleted}</td>
                                    <td>{r.criticalCompletionPercent.toFixed(1)}%</td>
                                    <td>{r.criticalOnTime}</td>
                                    <td>{r.criticalOnTimePercent.toFixed(1)}%</td>
                                    <td>
                                        <span
                                            style={{
                                                padding: "4px 8px",
                                                borderRadius: "4px",
                                                backgroundColor:
                                                    r.competencyStatus === "Pass"
                                                        ? "#d4edda"
                                                        : r.competencyStatus === "Fail"
                                                          ? "#f8d7da"
                                                          : "#e2e3e5",
                                                color:
                                                    r.competencyStatus === "Pass"
                                                        ? "#155724"
                                                        : r.competencyStatus === "Fail"
                                                          ? "#721c24"
                                                          : "#383d41",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {r.competencyStatus}
                                        </span>
                                    </td>
                                    <td>
                                        {r.nonCriticalCompleted}/{r.nonCriticalAssigned}
                                    </td>
                                    <td>{r.quizzesAssigned}</td>
                                    <td>{r.quizzesCompleted}</td>
                                    <td>{r.passAttempt.a1}</td>
                                    <td>{r.passAttempt.a2}</td>
                                    <td>{r.passAttempt.a3}</td>
                                    <td>{r.passAttempt.a4}</td>
                                    <td>
                                        <Button size="sm" variant="primary" onClick={() => openUserDetail(r.user)}>
                                            Details
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Tab>
                <Tab eventKey="byDocument" title="By Document">
                    <Table striped bordered hover size="sm" className={TRAINING_KPI_TABLE}>
                        <thead>
                            <tr>
                                <th>Training</th>
                                <th>Version</th>
                                <th>Classification</th>
                                <th>Effective On</th>
                                <th>Total Trainees</th>
                                <th>Completed</th>
                                <th>Completion %</th>
                                <th>Completed On Time</th>
                                <th>On-Time %</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {documentSummaryRows.map((r) => {
                                const completionPercent =
                                    r.totalAssigned > 0 ? (r.completed / r.totalAssigned) * 100 : 0;
                                const onTimePercent =
                                    r.totalAssigned > 0 ? (r.onTimePass / r.totalAssigned) * 100 : 0;
                                return (
                                    <tr key={r.key}>
                                        <td>{r.documentName}</td>
                                        <td>{r.version}</td>
                                        <td>{r.classification}</td>
                                        <td>{r.effectiveOnDate ? formatDate(r.effectiveOnDate) : ""}</td>
                                        <td>{r.totalAssigned}</td>
                                        <td>{r.completed}</td>
                                        <td>{completionPercent.toFixed(1)}%</td>
                                        <td>{r.onTimePass}</td>
                                        <td>{onTimePercent.toFixed(1)}%</td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={() => {
                                                    setDocUserSelectedIds(new Set());
                                                    setDocUserModalKey(r.key);
                                                }}
                                            >
                                                Manage classification
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Tab>
            </Tabs>

            {/* KPI Report Modal */}
            <Modal show={showKpiModal} onHide={() => setShowKpiModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Training Competency KPI Report</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 16 }}>
                        <div>
                            <p style={{ color: "#666", margin: 0 }}>Select trainees to include in the KPI calculation.</p>
                            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                                <Form.Group style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 0 }}>
                                    <Form.Label style={{ marginBottom: 0, fontSize: 12 }}>From</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => handleFromDateChange(e.target.value)}
                                        style={{ maxWidth: 150 }}
                                    />
                                </Form.Group>
                                <Form.Group style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 0 }}>
                                    <Form.Label style={{ marginBottom: 0, fontSize: 12 }}>To</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => handleToDateChange(e.target.value)}
                                        style={{ maxWidth: 150 }}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                        <Button variant="success" size="sm" onClick={exportSummaryExcel}>
                            Export Excel
                        </Button>
                    </div>

                    <div
                        style={{
                            marginBottom: 12,
                            padding: 8,
                            borderRadius: 4,
                            border: "1px solid #dee2e6",
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                        }}
                    >
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <Button variant="outline-secondary" size="sm" onClick={handleKpiModalClear}>
                                Clear
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={handleKpiModalSelectAllNonNA}>
                                Select all (exclude N/A)
                            </Button>
                            <div style={{ fontSize: 12, color: "#666" }}>
                                Selected: <strong>{kpiModalSelectedIds.size}</strong>
                            </div>
                        </div>
                        {(() => {
                            const allOptions = kpiModalOptions.map((r) => ({
                                value: r.user.accountId,
                                label: `${r.user.displayName} - ${r.competencyStatus}`,
                            }));
                            const selectedValues = new Set(kpiModalSelectedIds);
                            const selectedOptions = allOptions.filter((o) => selectedValues.has(o.value));
                            return (
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={allOptions}
                                    onChange={handleKpiModalSelectChange}
                                    placeholder={allOptions.length ? "Select trainees..." : "No trainees available"}
                                    styles={{
                                        container: (base) => ({ ...base, minWidth: 260 }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                    menuPortalTarget={document.body}
                                    value={selectedOptions}
                                />
                            );
                        })()}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <Table bordered size="sm" style={{ minWidth: 920 }}>
                            <thead>
                                <tr style={{ backgroundColor: "#6aab6a", color: "white", textAlign: "center" }}>
                                    <th style={{ width: 70 }}>Action</th>
                                    <th style={{ backgroundColor: "#a8bfd8" }}>Trainee</th>
                                    <th style={{ backgroundColor: "#a8bfd8" }}>Total Assigned</th>
                                    <th style={{ backgroundColor: "#a8bfd8" }}>
                                        Completed
                                        <br />
                                        <small>(%= Completed / Total Assigned × 100)</small>
                                    </th>
                                    <th style={{ backgroundColor: "#a8bfd8" }}>
                                        Completed On Time
                                        <br />
                                        <small>(%=Completed On Time / Total Assigned × 100)</small>
                                    </th>
                                    <th style={{ backgroundColor: "#f4d0c4" }}>Critical Assigned</th>
                                    <th style={{ backgroundColor: "#f4d0c4" }}>
                                        Critical Completed
                                        <br />
                                        <small>(%= Critical Completed / Critical Assigned × 100)</small>
                                    </th>
                                    <th style={{ backgroundColor: "#f4d0c4" }}>
                                        Critical On Time
                                        <br />
                                        <small>(% = Critical On Time / Critical Assigned × 100)</small>
                                    </th>
                                    <th style={{ backgroundColor: "#6aab6a", color: "white" }}>Training competency status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSummaryRows
                                    .filter((r) => kpiModalSelectedIds.has(r.user.accountId))
                                    .map((r) => {
                                        const completionPct =
                                            r.totalAssigned > 0 ? ((r.completed / r.totalAssigned) * 100).toFixed(0) : "0";
                                        const onTimePct = r.totalAssigned > 0 ? ((r.onTimePass / r.totalAssigned) * 100).toFixed(0) : "0";
                                        const critCompletionPct =
                                            r.criticalAssigned > 0 ? ((r.criticalCompleted / r.criticalAssigned) * 100).toFixed(0) : "–";
                                        const critOnTimePct =
                                            r.criticalAssigned > 0 ? ((r.criticalOnTime / r.criticalAssigned) * 100).toFixed(0) : "–";
                                        return (
                                            <tr key={r.user.accountId}>
                                                <td style={{ textAlign: "center" }}>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleKpiModalRemoveUser(r.user.accountId)}
                                                    >
                                                        X
                                                    </Button>
                                                </td>
                                                <td>{r.user.displayName}</td>
                                                <td style={{ textAlign: "center" }}>{r.totalAssigned}</td>
                                                <td style={{ textAlign: "center" }}>
                                                    {r.completed} ({completionPct}%)
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    {r.onTimePass} ({onTimePct}%)
                                                </td>
                                                <td style={{ textAlign: "center" }}>{r.criticalAssigned}</td>
                                                <td style={{ textAlign: "center" }}>
                                                    {r.criticalAssigned > 0 ? `${r.criticalCompleted} (${critCompletionPct}%)` : "–"}
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    {r.criticalAssigned > 0 ? `${r.criticalOnTime} (${critOnTimePct}%)` : "–"}
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    <span
                                                        style={{
                                                            padding: "2px 10px",
                                                            borderRadius: 4,
                                                            fontWeight: "bold",
                                                            backgroundColor:
                                                                r.competencyStatus === "Pass"
                                                                    ? "#d4edda"
                                                                    : r.competencyStatus === "Fail"
                                                                      ? "#f8d7da"
                                                                      : "#e2e3e5",
                                                            color:
                                                                r.competencyStatus === "Pass"
                                                                    ? "#155724"
                                                                    : r.competencyStatus === "Fail"
                                                                      ? "#721c24"
                                                                      : "#383d41",
                                                        }}
                                                    >
                                                        {r.competencyStatus}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                {/* KPI row */}
                                <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                                    <td></td>
                                    <td colSpan={2} style={{ textAlign: "center" }}>
                                        KPI
                                    </td>
                                    <td colSpan={7} style={{ textAlign: "center" }}>
                                        {kpiModalKPI ? (
                                            <>
                                                Pass rate (%) = (Passed employees / Total employees) × 100 &nbsp;=&nbsp;
                                                <span style={{ color: "#155724" }}>
                                                    ({kpiModalKPI.passed} / {kpiModalKPI.total}) × 100 ={" "}
                                                    <strong>{kpiModalKPI.passRate.toFixed(1)}%</strong>
                                                </span>
                                                &nbsp;&nbsp;
                                                <span style={{ color: "#666", fontSize: 13 }}>
                                                    ({kpiModalKPI.passed} Passed, {kpiModalKPI.failed} Failed)
                                                </span>
                                            </>
                                        ) : (
                                            <span style={{ color: "#999" }}>Select at least one trainee</span>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowKpiModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Per-document user classification modal */}
            <Modal show={!!docUserModalKey} onHide={() => setDocUserModalKey(null)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Document classification by user
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(() => {
                        const docSummary = documentSummaryRows.find((d) => d.key === docUserModalKey);
                        return (
                            <div style={{ marginBottom: 12 }}>
                                <div>
                                    <strong>Training:</strong> {docSummary?.documentName || ""}
                                </div>
                                <div>
                                    <strong>Version:</strong> {docSummary?.version || ""} &nbsp;|&nbsp;
                                    <strong>Current base classification:</strong> {docSummary?.classification || ""}
                                </div>
                            </div>
                        );
                    })()}

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                        <Form.Label style={{ marginBottom: 0 }}>Bulk classification override for selected users:</Form.Label>
                        <Form.Select
                            size="sm"
                            style={{ maxWidth: 200 }}
                            value={docUserBulkClassification}
                            onChange={(e) => setDocUserBulkClassification(e.target.value as Classification)}
                        >
                            <option value="CRITICAL">Critical</option>
                            <option value="NON_CRITICAL">Non-Critical</option>
                        </Form.Select>
                        <Button
                            size="sm"
                            variant="success"
                            disabled={docUserSelectedIds.size === 0 || docUserUpdating}
                            onClick={async () => {
                                const ids = Array.from(docUserSelectedIds);
                                if (!ids.length) return;
                                setDocUserUpdating(true);
                                try {
                                    await Promise.all(
                                        ids.map((id) =>
                                            updateTrainingClassificationOverrideAPI({
                                                assignedId: id,
                                                trainingClassificationOverride: docUserBulkClassification,
                                            }),
                                        ),
                                    );
                                    await dispatch(getAllUsersThunk());
                                    setDocUserSelectedIds(new Set());
                                } finally {
                                    setDocUserUpdating(false);
                                }
                            }}
                        >
                            Apply to selected ({docUserSelectedIds.size})
                        </Button>
                    </div>

                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>
                                    <Form.Check
                                        type="checkbox"
                                        checked={
                                            docUserModalRows.length > 0 &&
                                            docUserModalRows.every((r) => r.assigned._id && docUserSelectedIds.has(r.assigned._id))
                                        }
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const next = new Set<string>();
                                                docUserModalRows.forEach((r) => {
                                                    if (r.assigned._id) next.add(r.assigned._id);
                                                });
                                                setDocUserSelectedIds(next);
                                            } else {
                                                setDocUserSelectedIds(new Set());
                                            }
                                        }}
                                    />
                                </th>
                                <th>Employee</th>
                                <th>Current classification</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docUserModalRows.map((r) => (
                                <tr key={r.assigned._id || r.user.accountId}>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            disabled={!r.assigned._id}
                                            checked={!!(r.assigned._id && docUserSelectedIds.has(r.assigned._id))}
                                            onChange={(e) => {
                                                if (!r.assigned._id) return;
                                                setDocUserSelectedIds((prev) => {
                                                    const next = new Set(prev);
                                                    if (e.target.checked) next.add(r.assigned._id!);
                                                    else next.delete(r.assigned._id!);
                                                    return next;
                                                });
                                            }}
                                        />
                                    </td>
                                    <td>{r.user.displayName}</td>
                                    <td>{r.classification}</td>
                                </tr>
                            ))}
                            {!docUserModalRows.length && (
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "center", color: "#666" }}>
                                        No assignments in the current date range for this document.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDocUserModalKey(null)} disabled={docUserUpdating}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDetail} onHide={() => setShowDetail(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Employee detail: {selectedUser?.displayName} (completion date range: {formatDate(fromDateObj)} to{" "}
                        {formatDate(toDateObj)})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Training</th>
                                <th>Version</th>
                                <th>Type</th>
                                <th>Classification</th>
                                <th>Effective On</th>
                                <th>Sign date</th>
                                <th>Status (as-of)</th>
                                <th>Completion date</th>
                                <th>Days (complete/open)</th>
                                <th>On-time (effective)</th>
                                <th>Pass attempt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assignedDetailRows.map((r) => {
                                const riskClass = r.risk === "RED" ? "risk-red" : r.risk === "YELLOW" ? "risk-yellow" : "";
                                const badgeClass =
                                    r.onTime.result === "PASS"
                                        ? "kpi-badge pass"
                                        : r.onTime.result === "FAIL"
                                          ? "kpi-badge fail"
                                          : "kpi-badge pending";
                                return (
                                    <tr key={(r.assigned._id || "") + r.name + r.version} className={riskClass}>
                                        <td>{r.name}</td>
                                        <td>{r.version}</td>
                                        <td>{r.quizType}</td>
                                        <td style={{ width: 180 }}>
                                            <Form.Select
                                                size="sm"
                                                value={(r.assigned.trainingClassificationOverride || "AUTO") as any}
                                                onChange={(e) => handleClassificationOverrideChange(r.assigned, e.target.value as any)}
                                                disabled={!r.assigned._id}
                                            >
                                                <option value="AUTO">Auto ({r.baseTrainingClassification})</option>
                                                <option value="CRITICAL">Critical</option>
                                                <option value="NON_CRITICAL">Non-Critical</option>
                                            </Form.Select>
                                        </td>
                                        <td>{r.effectiveOnDate ? formatDate(r.effectiveOnDate) : ""}</td>
                                        <td>{r.signDate ? formatDate(r.signDate) : ""}</td>
                                        <td>{r.statusAsOf}</td>
                                        <td>{r.completionDate ? formatDate(r.completionDate) : ""}</td>
                                        <td>{typeof r.onTime.days === "number" ? r.onTime.days : ""}</td>
                                        <td style={{ width: 220 }}>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                <span className={badgeClass}>
                                                    {r.onTime.result}
                                                    {r.onTime.isOverridden ? " (override)" : ""}
                                                </span>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <Form.Select
                                                        size="sm"
                                                        value={(r.assigned.onTimeOverride ? r.assigned.onTimeOverride : "AUTO") as any}
                                                        onChange={(e) => handleOverrideChange(r.assigned, e.target.value as any)}
                                                        disabled={!r.assigned._id || updatingOnTimeId === r.assigned._id}
                                                    >
                                                        <option value="AUTO">Auto</option>
                                                        <option value="PASS">Pass</option>
                                                        <option value="FAIL">Fail</option>
                                                    </Form.Select>
                                                    {updatingOnTimeId === r.assigned._id && (
                                                        <Spinner animation="border" size="sm" role="status">
                                                            <span className="visually-hidden">Saving...</span>
                                                        </Spinner>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{r.attempt || ""}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Modal.Body>
            </Modal>
        </div>
    );
}

function formatDate(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
}

function parseDateSafe(dateLike?: any): Date | null {
    if (!dateLike) return null;
    const d = new Date(dateLike);
    return isNaN(d.getTime()) ? null : d;
}

function diffDays(from: Date, to: Date): number {
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((to.getTime() - from.getTime()) / msPerDay);
}

function hasDatedTracks(assigned: AssignedAny): boolean {
    return (assigned.resultHistoryTrack || []).some((t: any) => !!t?.date);
}

function getStatusAsOf(assigned: AssignedAny, asOf: Date): string | null {
    const signDate = parseDateSafe(assigned.createdAt);
    if (!signDate || signDate.getTime() > asOf.getTime()) return null;

    if (hasDatedTracks(assigned)) {
        const tracks = (assigned.resultHistoryTrack || [])
            .map((t: any) => ({ ...t, _d: parseDateSafe(t.date) }))
            .filter((t: any) => !!t._d && (t._d as Date).getTime() <= asOf.getTime())
            .sort((a: any, b: any) => (a._d as Date).getTime() - (b._d as Date).getTime());

        if (!tracks.length) return STATUS_ASSIGNED;
        return tracks[tracks.length - 1].status || STATUS_ASSIGNED;
    }

    return assigned.status || STATUS_ASSIGNED;
}

function getCompletionDateAsOf(assigned: AssignedAny, asOf: Date): Date | null {
    const signDate = parseDateSafe(assigned.createdAt);
    if (!signDate || signDate.getTime() > asOf.getTime()) return null;

    if (hasDatedTracks(assigned)) {
        const tracks = (assigned.resultHistoryTrack || [])
            .map((t: any) => ({ ...t, _d: parseDateSafe(t.date) }))
            .filter((t: any) => !!t._d && (t._d as Date).getTime() <= asOf.getTime())
            .sort((a: any, b: any) => (a._d as Date).getTime() - (b._d as Date).getTime());
        const passed = tracks.find((t: any) => t.status === STATUS_PASSED);
        return passed?._d || null;
    }

    if (assigned.status === STATUS_PASSED) {
        const upd = parseDateSafe(assigned.updatedAt);
        if (upd && upd.getTime() <= asOf.getTime()) return upd;
    }
    return null;
}

function getOnTimeBaseResult(
    assigned: AssignedAny,
    asOf: Date,
    deadlineDays = 30,
    baseDate?: Date,
): { result: OnTimeResult; days?: number } {
    const signDate = baseDate ?? parseDateSafe(assigned.createdAt);
    if (!signDate) return { result: "PENDING" };

    const completionDate = getCompletionDateAsOf(assigned, asOf);
    if (completionDate) {
        const daysToComplete = diffDays(signDate, completionDate);
        return { result: daysToComplete <= deadlineDays ? "PASS" : "FAIL", days: daysToComplete };
    }

    const daysOpen = diffDays(signDate, asOf);
    if (daysOpen > deadlineDays) return { result: "FAIL", days: daysOpen };
    return { result: "PENDING", days: daysOpen };
}

function getOnTimeEffectiveResult(
    assigned: AssignedAny,
    asOf: Date,
    deadlineDays = 30,
    baseDate?: Date,
): { result: OnTimeResult; days?: number; isOverridden: boolean } {
    const base = getOnTimeBaseResult(assigned, asOf, deadlineDays, baseDate);
    if (assigned.onTimeOverride === "PASS") return { result: "PASS", days: base.days, isOverridden: true };
    if (assigned.onTimeOverride === "FAIL") return { result: "FAIL", days: base.days, isOverridden: true };
    return { ...base, isOverridden: false };
}

function getRiskColor(daysOpen: number): "RED" | "YELLOW" | "NONE" {
    if (daysOpen >= 30) return "RED";
    if (daysOpen >= 25) return "YELLOW";
    return "NONE";
}

function getAttemptPassedOn(assigned: AssignedAny): number | null {
    const tracks = (assigned.resultHistoryTrack || []).filter((t: any) => t.status === STATUS_PASSED || t.status === STATUS_FAILED);
    const firstPassIdx = tracks.findIndex((t: any) => t.status === STATUS_PASSED);
    if (firstPassIdx === -1) return null;
    return firstPassIdx + 1;
}

function docVersionKey(documentId: string, version: string): string {
    return `${documentId}::${version}`;
}

function buildClassificationMap(documents: DocumentDto[]): Map<string, Classification> {
    const m = new Map<string, Classification>();
    documents.forEach((doc: any) => {
        (doc.allVersions || []).forEach((v: any) => {
            m.set(docVersionKey(doc.confluenceDocumentId, v.version), v.trainingClassification || "NON_CRITICAL");
        });
    });
    return m;
}

function buildEffectiveOnMap(documents: DocumentDto[]): Map<string, Date> {
    console.log("documents", documents);
    const m = new Map<string, Date>();
    (documents as any[]).forEach((doc: any) => {
        (doc.allVersions || []).forEach((v: any) => {
            const raw = (v as any).effectiveOn;
            if (!raw) return;
            const d = parseDateSafe(raw);
            if (d) {
                m.set(docVersionKey(doc.confluenceDocumentId, v.version), d);
            }
        });
    });
    return m;
}

function isQuizAssigned(assigned: AssignedAny): boolean {
    return assigned.documentVersion.quizType === QUIZ_TYPE_MCQ;
}

function isCompletedAsOf(assigned: AssignedAny, asOf: Date): boolean {
    return getStatusAsOf(assigned, asOf) === STATUS_PASSED;
}

function filterAssignedAsOf(user: UserDto, asOf: Date): AssignedDto[] {
    return (user.assigned || []).filter((a: any) => {
        const signDate = parseDateSafe(a.createdAt);
        return !!signDate && signDate.getTime() <= asOf.getTime();
    });
}

function filterAssignedInRange(user: UserDto, fromDate: Date, toDate: Date): AssignedDto[] {
    // Timeline is based on completion date: include only assignments completed within the date range
    return (user.assigned || []).filter((a: any) => {
        const completionDate = getCompletionDateAsOf(a as AssignedAny, toDate);
        if (!completionDate) return false;
        return completionDate.getTime() >= fromDate.getTime() && completionDate.getTime() <= toDate.getTime();
    });
}

function getStatusInRange(assigned: AssignedAny, fromDate: Date, toDate: Date): string | null {
    const signDate = parseDateSafe(assigned.createdAt);
    if (!signDate) return null;

    if (hasDatedTracks(assigned)) {
        const tracks = (assigned.resultHistoryTrack || [])
            .map((t: any) => ({ ...t, _d: parseDateSafe(t.date) }))
            .filter((t: any) => !!t._d && (t._d as Date).getTime() <= toDate.getTime())
            .sort((a: any, b: any) => (a._d as Date).getTime() - (b._d as Date).getTime());

        if (!tracks.length) return STATUS_ASSIGNED;
        const lastTrack = tracks[tracks.length - 1];
        // Only return status if the track date is within range or if sign date is before range
        if (signDate.getTime() < fromDate.getTime() || (lastTrack._d as Date).getTime() >= fromDate.getTime()) {
            return lastTrack.status || STATUS_ASSIGNED;
        }
        return STATUS_ASSIGNED;
    }

    if (signDate.getTime() < fromDate.getTime()) {
        return assigned.status || STATUS_ASSIGNED;
    }
    return assigned.status || STATUS_ASSIGNED;
}

function getCompletionDateInRange(assigned: AssignedAny, fromDate: Date, toDate: Date): Date | null {
    const signDate = parseDateSafe(assigned.createdAt);
    if (!signDate) return null;

    if (hasDatedTracks(assigned)) {
        const tracks = (assigned.resultHistoryTrack || [])
            .map((t: any) => ({ ...t, _d: parseDateSafe(t.date) }))
            .filter((t: any) => !!t._d && (t._d as Date).getTime() <= toDate.getTime() && (t._d as Date).getTime() >= fromDate.getTime())
            .sort((a: any, b: any) => (a._d as Date).getTime() - (b._d as Date).getTime());
        const passed = tracks.find((t: any) => t.status === STATUS_PASSED);
        if (passed?._d) return passed._d;

        // If sign date is before range, check if there was a completion before range
        if (signDate.getTime() < fromDate.getTime()) {
            const allTracks = (assigned.resultHistoryTrack || [])
                .map((t: any) => ({ ...t, _d: parseDateSafe(t.date) }))
                .filter((t: any) => !!t._d && (t._d as Date).getTime() <= toDate.getTime())
                .sort((a: any, b: any) => (a._d as Date).getTime() - (b._d as Date).getTime());
            const passedBefore = allTracks.find((t: any) => t.status === STATUS_PASSED);
            return passedBefore?._d || null;
        }
        return null;
    }

    if (assigned.status === STATUS_PASSED) {
        const upd = parseDateSafe(assigned.updatedAt);
        if (upd && upd.getTime() <= toDate.getTime()) {
            // If sign date is before range, include completion even if it's before range start
            if (signDate.getTime() < fromDate.getTime() || upd.getTime() >= fromDate.getTime()) {
                return upd;
            }
        }
    }
    return null;
}

function isCompletedInRange(assigned: AssignedAny, fromDate: Date, toDate: Date): boolean {
    const completionDate = getCompletionDateInRange(assigned, fromDate, toDate);
    return !!completionDate;
}

function getOnTimeBaseResultInRange(
    assigned: AssignedAny,
    fromDate: Date,
    toDate: Date,
    deadlineDays = 30,
): { result: OnTimeResult; days?: number } {
    const signDate = parseDateSafe(assigned.createdAt);
    if (!signDate) return { result: "PENDING" };

    const completionDate = getCompletionDateInRange(assigned, fromDate, toDate);
    if (completionDate) {
        const daysToComplete = diffDays(signDate, completionDate);
        return { result: daysToComplete <= deadlineDays ? "PASS" : "FAIL", days: daysToComplete };
    }

    // Check if still pending at end of range
    const daysOpen = diffDays(signDate, toDate);
    if (daysOpen > deadlineDays) return { result: "FAIL", days: daysOpen };
    return { result: "PENDING", days: daysOpen };
}

function getOnTimeEffectiveResultInRange(
    assigned: AssignedAny,
    fromDate: Date,
    toDate: Date,
    deadlineDays = 30,
): { result: OnTimeResult; days?: number; isOverridden: boolean } {
    const base = getOnTimeBaseResultInRange(assigned, fromDate, toDate, deadlineDays);
    if (assigned.onTimeOverride === "PASS") return { result: "PASS", days: base.days, isOverridden: true };
    if (assigned.onTimeOverride === "FAIL") return { result: "FAIL", days: base.days, isOverridden: true };
    return { ...base, isOverridden: false };
}

function downloadBlob(content: string, filename: string, contentType: string) {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function escapeHtml(s: string) {
    return s.replace(/[&<>"']/g, (c) => {
        switch (c) {
            case "&":
                return "&amp;";
            case "<":
                return "&lt;";
            case ">":
                return "&gt;";
            case '"':
                return "&quot;";
            case "'":
                return "&#039;";
            default:
                return c;
        }
    });
}

export default TrainingKPIPage;
